import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTimes, FaCheckCircle, FaExclamationCircle, FaCheck, FaSearch, FaFlask, FaTrash, FaPlus, FaUserPlus, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { getCurrentRole } from '../utils/medicineHelpers';
import { getDiagnosticAddresses, saveDiagnosticAddress, updateDiagnosticAddress, checkDiagnosticServiceability, addFamilyMember, updateFamilyMember, getDiagnosticExistingOrder, getDiagnosticTestDetail, saveDiagnosticCollection } from '../services/subscriberWalletService';
// TEMPORARY (testing): internal pincode API disabled — using the open api.postalpincode.in
// API inside lookupPincode below. TODO: re-enable this import before shipping.
// import { lookupPincodeDetail } from '../services/hospitalService';
import DiagnosticGeoFields, { latError, lngError } from '../components/diagnostic/DiagnosticGeoFields';
import DiagnosticPartnerCard from '../components/diagnostic/DiagnosticPartnerCard';
import { formatRelationshipForApi, formatGenderForApi, calculateAgeFromDob } from '../utils/subscriberHelpers';
import AddFamilyMemberModal from '../components/subscriber/AddFamilyMemberModal';
import { useDiagnosticOrder } from '../hooks/useDiagnosticOrder';
import { useDiagnosticPatients } from '../hooks/useDiagnosticPatients';
import { useDiagnosticTests } from '../hooks/useDiagnosticTests';
import { useDiagnosticSlots } from '../hooks/useDiagnosticSlots';
import { useDiagnosticCart } from '../hooks/useDiagnosticCart';
import { useBookingPaymentLink } from '../hooks/useBookingPaymentLink';
import PaymentLinkCard from '../components/common/PaymentLinkCard';

/* ─── step slide animation ────────────────────────────────────────────────── */
const stepSlideStyles = `
  @keyframes slideInFromRight {
    from { opacity: 0; transform: translateX(48px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideInFromLeft {
    from { opacity: 0; transform: translateX(-48px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .step-slide-forward { animation: slideInFromRight 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }
  .step-slide-back    { animation: slideInFromLeft  0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }

  @keyframes macOSOpen {
    0%   { opacity: 0; transform: scale(0.72) translateY(6px); filter: blur(4px); }
    60%  { opacity: 1; transform: scale(1.02) translateY(-1px); filter: blur(0); }
    100% { opacity: 1; transform: scale(1)    translateY(0);    filter: blur(0); }
  }
  @keyframes macOSBackdrop {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .macos-open    { animation: macOSOpen    0.32s cubic-bezier(0.34, 1.4, 0.64, 1) both; }
  .macos-backdrop { animation: macOSBackdrop 0.22s ease both; }

  .orange-scroll::-webkit-scrollbar { width: 4px; }
  .orange-scroll::-webkit-scrollbar-track { background: transparent; }
  .orange-scroll::-webkit-scrollbar-thumb { background: #fed7aa; border-radius: 99px; }
  .orange-scroll::-webkit-scrollbar-thumb:hover { background: #fb923c; }
`;

/* ─── theme token ─────────────────────────────────────────────────────────── */
// Orange/amber theme — distinct from VC (purple) and medicine (blue).
const T = {
  ring: 'focus-visible:ring-orange-500',
  spinner: 'border-orange-500',
  stepDone: 'bg-orange-500 text-white',
  stepCur: 'bg-orange-50 text-orange-800 border border-orange-500',
  stepFut: 'bg-gray-100 text-gray-400',
  stepLabel: 'text-orange-700',
  btn: 'bg-orange-500 text-white hover:bg-orange-600',
  btnDis: 'bg-gray-200 text-gray-400 cursor-not-allowed',
  chip: 'bg-orange-100 text-orange-800 hover:bg-orange-200',
  slotSel: 'bg-orange-500 text-white border-orange-500',
  slotDef: 'bg-white text-gray-700 border-gray-300 hover:border-orange-400 hover:bg-orange-50',
  success: 'bg-orange-50 border-orange-200',
  successIc: 'bg-orange-100',
  successTx: 'text-orange-600',
  tag: 'bg-orange-50 border border-orange-200 text-orange-900',
  label: 'text-xs uppercase tracking-wide text-gray-500',
};

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const STEPS = ['Partner & Address', 'Select Patients', 'Add Tests', 'Choose Slot', 'Review & Push'];

const fmt = (n) => `₹${(Number(n) || 0).toFixed(2)}`;

const capFirst = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h, 10);
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
};

const getNextDates = (n = 7) => {
  const dates = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    dates.push(d);
  }
  return dates;
};


// TEMPORARY (testing): pincode lookup via the open api.postalpincode.in API.
// Returns the same shape as the internal version so the dropdown/state code is unchanged.
// TODO: revert to the internal pincode API (commented block below) before shipping.
const lookupPincode = async (pin) => {
  const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
  const data = await res.json();
  if (data?.[0]?.Status !== 'Success' || !data[0]?.PostOffice?.length) throw new Error('not found');
  const offices = data[0].PostOffice;
  const po = offices[0];
  // Post-office names for the pincode → the sublocality dropdown options.
  const sublocalityOptions = [...new Set(offices.map(o => o.Name).filter(Boolean))];
  return {
    city: po.District || po.Block || '',
    state: po.State || '',
    sublocality: sublocalityOptions[0] || po.Name || '',
    sublocalityOptions,
  };

  /* INTERNAL pincode API — re-enable this (and the import at the top of the file) after testing:
  const msg = await lookupPincodeDetail(pin);
  const locations = msg?.locations || [];
  const loc = locations[0];
  if (!loc) throw new Error('not found');
  const sublocalityOptions = [...new Set(locations.map(l => l.city).filter(Boolean))];
  return {
    city: loc.district || loc.city || '',
    state: loc.state || '',
    sublocality: sublocalityOptions[0] || loc.city || '',
    sublocalityOptions,
  };
  */
};

/* ─── component ───────────────────────────────────────────────────────────── */
const DiagnosticTransaction = ({ applicationId }) => {
  const { id: idFromParams } = useParams();
  const id = applicationId || idFromParams;
  const navigate = useNavigate();
  const currentRole = getCurrentRole();
  const canAccess = currentRole === 'Ops' || currentRole === 'SuperAdmin';

  const [notification, setNotification] = useState(null);
  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  /* ── step ── */
  const [step, setStep] = useState(1); // 1-6 (6=success)
  const prevStepRef = useRef(1);
  const slideDir = prevStepRef.current <= step ? 'step-slide-forward' : 'step-slide-back';
  const goToStep = (n) => { prevStepRef.current = step; setStep(n); };

  /* ── step 1: partner + address ── */
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const addressesLoadedRef = useRef(false);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  // AyushPay-owned labs carry an `address`; partners (e.g. Healthians) come through with
  // `address: null`. AyushPay labs price at a single MRP (no discount), so the catalog/added
  // rows show one price for them; partners keep the struck-MRP + discounted-price pair.
  const [isAyushpayLab, setIsAyushpayLab] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [serviceableVendors, setServiceableVendors] = useState(null); // null = not checked yet
  const [checkingServiceability, setCheckingServiceability] = useState(false);
  const [addr, setAddr] = useState({ address_line_1: '', city: '', state: '', pincode: '', landmark: '', sublocality: '', address_type: 'home', latitude: '', longitude: '' });
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeError, setPincodeError] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editAddr, setEditAddr] = useState({ address_line_1: '', city: '', state: '', pincode: '', landmark: '', sublocality: '', address_type: 'home', latitude: '', longitude: '' });
  const [editPincodeLoading, setEditPincodeLoading] = useState(false);
  const [editPincodeError, setEditPincodeError] = useState(false);
  const [updatingAddress, setUpdatingAddress] = useState(false);

  const pincodeAbortRef = useRef(null);
  const editPincodeAbortRef = useRef(null);
  const [sublocalityOptions, setSublocalityOptions] = useState([]);
  const [editSublocalityOptions, setEditSublocalityOptions] = useState([]);

  const fetchPincodeData = useCallback(async (pin) => {
    if (pin.length !== 6) { setPincodeError(false); setSublocalityOptions([]); return; }
    if (pincodeAbortRef.current) pincodeAbortRef.current.abort();
    const controller = new AbortController();
    pincodeAbortRef.current = controller;
    setPincodeLoading(true);
    setPincodeError(false);
    try {
      const result = await lookupPincode(pin);
      if (controller.signal.aborted) return; // a newer pincode is in flight
      setSublocalityOptions(result.sublocalityOptions || []);
      setAddr(p => ({ ...p, city: result.city || p.city, state: result.state || p.state, sublocality: result.sublocality || p.sublocality }));
    } catch (e) {
      if (!controller.signal.aborted) { setPincodeError(true); setSublocalityOptions([]); }
    }
    finally { if (!controller.signal.aborted) setPincodeLoading(false); }
  }, []);
  const [creatingOrder, setCreatingOrder] = useState(false);

  /* ── step 2: add member modal ── */
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [addApiErrors, setAddApiErrors] = useState({});
  const [editingMember, setEditingMember] = useState(null);
  const [editApiErrors, setEditApiErrors] = useState({});

  /* ── step 3: test search ── */
  const [testQuery, setTestQuery] = useState('');
  const [activeMemberId, setActiveMemberId] = useState('');
  const [localMembers, setLocalMembers] = useState([]);
  const [expandedTestId, setExpandedTestId] = useState(null);
  const [testDetailCache, setTestDetailCache] = useState({});
  const [testDetailLoading, setTestDetailLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  // Collection mode: 'home_collection' | 'walkin' | '' (none chosen yet)
  const [collectionType, setCollectionType] = useState('');
  const [savingCollection, setSavingCollection] = useState(false);

  /* ── step 4: slot inputs ── */
  const [pickedDate, setPickedDate] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [manualTime, setManualTime] = useState('');
  const savedSlotIdRef = useRef('');

  /* ── hooks ── */
  const { orderId, orderNumber, setOrderId, setOrderNumber, setZone, createOrder, submitOrder, submitting } = useDiagnosticOrder(currentRole);
  const { members, selectedMemberIds, loadingPatients, savingPatients, loadPatients, toggleMember, savePatients } = useDiagnosticPatients(currentRole);
  const { searchResults, searching, searchTests, addTest, removeTest, setSearchResults } = useDiagnosticTests(currentRole);
  const { slots, slotType, selectedSlot, setSelectedSlot, loadingSlots, savingSlot, loadSlots, saveSlot } = useDiagnosticSlots(currentRole);
  const { cart, loadCart } = useDiagnosticCart(currentRole);
  const { generatingLink, paymentLink, generateLink } = useBookingPaymentLink(currentRole);

  /* ── charge line label ──
     AyushPay-owned labs bill a phlebotomist fee (computed server-side from the lab's
     VendorOperation, waived once the subtotal clears phlebo_waiver), so their line reads
     "Phlebo Charge". Healthians and other partners keep the flat "Delivery" charge. The
     amount always comes straight from the cart — the backend owns the calculation. */
  const chargeLine = useMemo(() => {
    if (!cart) return null;
    const selectedVendor = (serviceableVendors || []).find(v => String(v.id) === String(selectedVendorId));
    const vendorIsAyushpayLab = selectedVendor?.is_ayushpay_lab ?? isAyushpayLab;
    return {
      label: vendorIsAyushpayLab ? 'Phlebo Charge' : 'Delivery',
      amount: Number(cart.delivery_charges) || 0,
      isAyushpayLab: vendorIsAyushpayLab,
    };
  }, [cart, serviceableVendors, selectedVendorId, isAyushpayLab]);

  /* ── load saved addresses on mount ── */
  useEffect(() => {
    if (addressesLoadedRef.current) return;
    addressesLoadedRef.current = true;
    (async () => {
      setLoadingAddresses(true);
      try {
        const addrRes = await getDiagnosticAddresses(currentRole, id);
        if (addrRes?.metadata?.api_status === true) {
          const addresses = addrRes.msg?.addresses || [];
          setSavedAddresses(addresses);
          if (addresses.length > 0) {
            setSelectedAddressId(addresses[0].id);
          } else {
            setShowNewAddressForm(true);
          }
        } else {
          setShowNewAddressForm(true);
        }
      } catch {
        setShowNewAddressForm(true);
      } finally {
        setLoadingAddresses(false);
      }
    })();
  }, [currentRole, id, showNotification]);


  /* ── prefill from existing order when vendor is selected ── */
  const prefillDoneRef = useRef(false);
  useEffect(() => {
    if (!selectedVendorId || !id) return;
    if (prefillDoneRef.current) return;
    prefillDoneRef.current = true;
    (async () => {
      try {
        const res = await getDiagnosticExistingOrder(currentRole, id, selectedVendorId);
        if (res?.metadata?.api_status === true && res.msg?.order) {
          const order = res.msg.order;
          setOrderId(order.id);
          setOrderNumber(order.order_number || '');
          setZone(order.zone || '');
          if (order.address) {
            setAddr({
              address_line_1: order.address.address_line_1 || '',
              city: order.address.city || '',
              state: order.address.state || '',
              pincode: order.address.pincode || '',
              landmark: order.address.landmark || '',
              sublocality: order.address.sublocality || '',
            });
          }
          if (order.slot_date) { setPickedDate(order.slot_date); setManualDate(order.slot_date); }
          if (order.slot_time) setManualTime(order.slot_time);
          if (order.slot) {
            savedSlotIdRef.current = String(order.slot);
            setSelectedSlot({ slot_id: String(order.slot), label: String(order.slot), slot_time: order.slot_time || '' });
          }
          if (order.home_collection) setCollectionType('home_collection');
          else if (order.walkin) setCollectionType('walkin');
          const hasMembers = Array.isArray(order.members) && order.members.length > 0;
          const hasTests = hasMembers && order.members.some(m => (m.products || []).length > 0);
          if (hasMembers) {
            setLocalMembers(order.members);
            setActiveMemberId(order.members[0]?.id || '');
            await loadPatients(order.id, showNotification);
          }
          if (order.slot_date) {
            await loadSlots(order.id, order.slot_date, showNotification);
            await loadCart(order.id, showNotification);
            goToStep(5);
          } else if (hasTests) {
            goToStep(4);
          } else if (hasMembers) {
            goToStep(3);
          } else {
            await loadPatients(order.id, showNotification);
            goToStep(2);
          }
        }
      } catch { /* no existing order — user stays on step 1 */ }
    })();
  }, [selectedVendorId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── reload selectable members when entering step 2 ── */
  useEffect(() => {
    if (step !== 2 || !orderId) return;
    loadPatients(orderId, showNotification, null, members.length > 0);
  }, [step, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── reload members+tests when entering step 3 ── */
  useEffect(() => {
    if (step !== 3 || !orderId || !selectedVendorId) return;
    (async () => {
      try {
        const res = await getDiagnosticExistingOrder(currentRole, id, selectedVendorId);
        if (res?.metadata?.api_status === true && res.msg?.order?.members) {
          const updated = res.msg.order.members;
          setLocalMembers(updated);
          if (!activeMemberId) setActiveMemberId(updated[0]?.id || '');
        }
      } catch { /* keep existing localMembers */ }
    })();
  }, [step, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── default to full test catalogue on entering step 3 (empty query = all) ── */
  useEffect(() => {
    if (step !== 3 || !orderId || !selectedVendorId) return;
    if (testQuery) return; // don't clobber an active search
    searchTests(orderId, '');
  }, [step, orderId, selectedVendorId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── load slots on step 4; prefill saved slot/date from API ── */
  useEffect(() => {
    if (step !== 4 || !orderId || !selectedVendorId) return;
    // If we already loaded slots for this date, don't re-fetch — preserves selection
    if (slots.length > 0 && pickedDate) return;
    (async () => {
      try {
        const res = await getDiagnosticExistingOrder(currentRole, id, selectedVendorId);
        if (res?.metadata?.api_status === true && res.msg?.order) {
          const order = res.msg.order;
          const dateToLoad = order.slot_date || pickedDate || getNextDates(1)[0].toISOString().slice(0, 10);
          if (order.slot_date) setPickedDate(order.slot_date);
          if (order.slot_time) setManualTime(order.slot_time);
          if (order.slot) {
            savedSlotIdRef.current = String(order.slot);
            setSelectedSlot({ slot_id: String(order.slot), label: String(order.slot), slot_time: order.slot_time || '' });
          }
          await loadSlots(orderId, dateToLoad, showNotification);
        }
      } catch {
        const firstDate = getNextDates(1)[0].toISOString().slice(0, 10);
        const dateToLoad = pickedDate || firstDate;
        if (!pickedDate) setPickedDate(dateToLoad);
        loadSlots(orderId, dateToLoad, showNotification);
      }
    })();
  }, [step, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── step handlers ────────────────────────────────────────────────────── */

  const KNOWN_ADDR_TYPES = ['home', 'office', 'other'];

  const handleEditAddressClick = (a) => {
    setEditingAddressId(a.id);
    const rawType = a.address_type || 'home';
    const knownType = KNOWN_ADDR_TYPES.includes(rawType) ? rawType : 'other';
    setEditAddr({
      address_line_1: a.address_line_1 || '',
      city: a.city || '',
      state: a.state || '',
      pincode: a.pincode || '',
      landmark: a.landmark || '',
      sublocality: a.sublocality || '',
      address_type: knownType,
      address_type_custom: knownType === 'other' ? rawType : '',
      latitude: a.latitude || '',
      longitude: a.longitude || '',
    });
    // Populate the sublocality dropdown for the saved pincode without clobbering the saved value.
    setEditSublocalityOptions([]);
    if (/^\d{6}$/.test(a.pincode || '')) {
      lookupPincode(a.pincode).then(r => setEditSublocalityOptions(r.sublocalityOptions || [])).catch(() => { });
    }
  };

  const handleUpdateAddress = async () => {
    if (!editAddr.address_line_1 || !editAddr.pincode || !editAddr.sublocality) {
      showNotification('Please fill all required address fields', 'error');
      return;
    }
    const resolvedEditType = editAddr.address_type === 'other' && editAddr.address_type_custom
      ? editAddr.address_type_custom.trim()
      : editAddr.address_type;
    const { address_type_custom: _skipEdit, ...editPayload } = editAddr;
    setUpdatingAddress(true);
    try {
      const res = await updateDiagnosticAddress(currentRole, editingAddressId, { ...editPayload, address_type: resolvedEditType });
      if (res?.metadata?.api_status === true) {
        const updated = res.msg?.address;
        setSavedAddresses(prev => prev.map(a => a.id === updated.id ? updated : a));
        setEditingAddressId(null);
        showNotification('Address updated', 'success');
      } else {
        showNotification('Failed to update address', 'error');
      }
    } catch {
      showNotification('Failed to update address', 'error');
    } finally {
      setUpdatingAddress(false);
    }
  };

  const handleCheckServiceability = async () => {
    if (!selectedAddressId) {
      showNotification('Please select an address first', 'error');
      return;
    }
    setCheckingServiceability(true);
    setServiceableVendors(null);
    setSelectedVendorId('');
    try {
      const res = await checkDiagnosticServiceability(currentRole, selectedAddressId, id);
      if (res?.metadata?.api_status === true) {
        setServiceableVendors(res.msg?.serviceable_vendors || []);
      } else {
        showNotification('Serviceability check failed', 'error');
      }
    } catch {
      showNotification('Serviceability check failed', 'error');
    } finally {
      setCheckingServiceability(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!addr.address_line_1 || !addr.pincode || !addr.sublocality) {
      showNotification('Please fill all required address fields', 'error');
      return;
    }
    const resolvedType = addr.address_type === 'other' && addr.address_type_custom
      ? addr.address_type_custom.trim()
      : addr.address_type;
    setSavingAddress(true);
    try {
      const { address_type_custom: _skip, ...addrPayload } = addr;
      const res = await saveDiagnosticAddress(currentRole, { application_id: id, ...addrPayload, address_type: resolvedType });
      if (res?.metadata?.api_status === true) {
        const newAddr = res.msg?.address;
        setSavedAddresses(prev => [newAddr, ...prev]);
        setSelectedAddressId(newAddr.id);
        setShowNewAddressForm(false);
        setAddr({ address_line_1: '', city: '', state: '', pincode: '', landmark: '', sublocality: '', address_type: 'home', latitude: '', longitude: '' });
        showNotification('Address saved', 'success');
      } else {
        showNotification('Failed to save address', 'error');
      }
    } catch {
      showNotification('Failed to save address', 'error');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!selectedVendorId) {
      showNotification('Please select a diagnostic partner', 'error');
      return;
    }
    if (!showNewAddressForm && !selectedAddressId) {
      showNotification('Please select an address', 'error');
      return;
    }
    if (showNewAddressForm && (!addr.address_line_1 || !addr.pincode || !addr.sublocality)) {
      showNotification('Please fill all required address fields', 'error');
      return;
    }
    setCreatingOrder(true);
    const resolvedAddrType = addr.address_type === 'other' && addr.address_type_custom
      ? addr.address_type_custom.trim()
      : addr.address_type;
    const { address_type_custom: _skip2, ...addrPayload2 } = addr;
    const orderData = showNewAddressForm
      ? { application_id: id, vendor_id: selectedVendorId, ...addrPayload2, address_type: resolvedAddrType }
      : { application_id: id, vendor_id: selectedVendorId, address_id: selectedAddressId };
    const newOrderId = await createOrder(orderData, showNotification);
    setCreatingOrder(false);
    if (newOrderId) {
      await loadPatients(newOrderId, showNotification);
      goToStep(2);
    }
  };

  const handleSavePatients = async () => {
    const selected = members.filter(m => selectedMemberIds.includes(m.id));

    // If a self member exists in the list, their email must be filled before continuing
    const selfMember = members.find(m => (m.relation || '').toLowerCase() === 'self');
    if (selfMember && !selfMember.email) {
      showNotification(`${selfMember.name || 'Self'}: Email is required for the self member`, 'error');
      return;
    }

    const REQUIRED = [
      { check: m => m.name, label: 'Name' },
      { check: m => m.dob || m.date_of_birth, label: 'Date of birth' },
      { check: m => m.relation, label: 'Relation' },
      { check: m => m.contact_number || m.phone || m.mobile, label: 'Phone number' },
      { check: m => m.gender, label: 'Gender' },
    ];
    const errors = [];
    selected.forEach(m => {
      const missing = REQUIRED.filter(f => !f.check(m)).map(f => f.label);
      if (missing.length) {
        errors.push(`${m.name || 'Patient'}: missing ${missing.join(', ')}`);
      } else {
        const age = calculateAgeFromDob(m.dob || m.date_of_birth);
        if (age !== null && age < 5) {
          errors.push(`${m.name || 'Patient'}: age must be at least 5 years`);
        }
      }
    });
    if (errors.length) {
      showNotification(errors.join(' | '), 'error');
      return;
    }
    const ok = await savePatients(orderId, showNotification);
    if (ok) {
      const withProducts = selected.map(m => ({ ...m, products: [] }));
      setLocalMembers(withProducts);
      setActiveMemberId(withProducts[0]?.id || '');
      goToStep(3);
    }
  };

  const handleEditMember = async (memberData) => {
    if (!editingMember) return;
    setAddingMember(true);
    setEditApiErrors({});
    try {
      const res = await updateFamilyMember(id, editingMember.id, currentRole, {
        name: memberData.name,
        relation: formatRelationshipForApi(memberData.relationship),
        gender: formatGenderForApi(memberData.gender),
        age: memberData.dob ? calculateAgeFromDob(memberData.dob) : memberData.age,
        dob: memberData.dob,
        contact_number: memberData.mobile,
        email: memberData.email,
      });
      if (res?.metadata?.api_status === true) {
        setEditingMember(null);
        setEditApiErrors({});
        showNotification('Member updated successfully', 'success');
        if (orderId) await loadPatients(orderId, showNotification, null, true);
      } else {
        const details = res?.msg?.details;
        const errMsg = res?.msg?.error;
        if (details && typeof details === 'object') {
          setEditApiErrors(details);
        } else if (errMsg) {
          showNotification(String(errMsg), 'error');
        } else {
          showNotification('Failed to update member', 'error');
        }
      }
    } catch (err) {
      try {
        const match = err.message.match(/:\s*(\{.*\})\s*$/s);
        if (match) {
          const parsed = JSON.parse(match[1]);
          const details = parsed?.msg?.details;
          const errMsg = parsed?.msg?.error;
          if (details && typeof details === 'object') {
            setEditApiErrors(details);
            return;
          } else if (errMsg) {
            showNotification(String(errMsg), 'error');
            return;
          }
        }
      } catch { /* fall through */ }
      showNotification(err.message || 'Failed to update member', 'error');
    } finally {
      setAddingMember(false);
    }
  };

  const handleAddTest = async (memberId, testData) => {
    const member = localMembers.find(m => m.id === memberId);
    const alreadyAdded = (member?.products || []).some(
      p => (p.product_id && p.product_id === testData.product_id) || p.name === testData.name
    );
    if (alreadyAdded) {
      showNotification('Test already added', 'error');
      return;
    }
    const updated = await addTest(orderId, memberId, testData, showNotification);
    if (updated) setLocalMembers(updated);
  };

  const handleRemoveTest = async (memberId, dealId) => {
    const updated = await removeTest(orderId, memberId, dealId, showNotification);
    if (updated) setLocalMembers(updated);
  };

  const handleToggleTestDetail = async (productId) => {
    if (expandedTestId === productId) {
      setExpandedTestId(null);
      return;
    }
    setExpandedTestId(productId);
    if (testDetailCache[productId]) return;
    setTestDetailLoading(true);
    try {
      const res = await getDiagnosticTestDetail(currentRole, orderId, productId);
      if (res?.metadata?.api_status === true) {
        setTestDetailCache(prev => ({ ...prev, [productId]: { ok: true, data: res.msg } }));
      } else {
        setTestDetailCache(prev => ({ ...prev, [productId]: { ok: false } }));
      }
    } catch {
      setTestDetailCache(prev => ({ ...prev, [productId]: { ok: false } }));
    } finally {
      setTestDetailLoading(false);
    }
  };

  const handleGoToSlots = async () => {
    const membersWithoutTests = localMembers.filter(m => (m.products || []).length === 0);
    if (membersWithoutTests.length > 0) {
      const names = membersWithoutTests.map(m => m.name).join(', ');
      showNotification(`Please add at least one test for: ${names}`, 'error');
      return;
    }
    // Collection type is an AyushPay-lab step only — partners (Healthians) keep
    // their own journey and never capture it.
    if (isAyushpayLab) {
      if (!collectionType) {
        showNotification('Please choose a collection type', 'error');
        return;
      }
      setSavingCollection(true);
      try {
        const res = await saveDiagnosticCollection(currentRole, orderId, {
          walkin: collectionType === 'walkin',
          home_collection: collectionType === 'home_collection',
        });
        if (res?.metadata?.api_status !== true) {
          showNotification(res?.msg?.error || 'Could not save collection type', 'error');
          return;
        }
      } catch {
        showNotification('Could not save collection type', 'error');
        return;
      } finally {
        setSavingCollection(false);
      }
    }
    goToStep(4);
  };

  const handleSaveSlot = async () => {
    if (slotType === 'manual') {
      if (!manualDate || !manualTime) {
        showNotification('Please enter date and time', 'error');
        return;
      }
      const ok = await saveSlot(orderId, { slot_date: manualDate, slot_time: manualTime }, showNotification);
      if (ok) {
        await loadCart(orderId, showNotification);
        goToStep(5);
      }
    } else {
      if (!selectedSlot || !pickedDate) {
        showNotification('Please select a date and slot', 'error');
        return;
      }
      const ok = await saveSlot(orderId, { ...selectedSlot, slot_date: pickedDate }, showNotification);
      if (ok) {
        await loadCart(orderId, showNotification);
        goToStep(5);
      }
    }
  };

  const handleSubmit = async () => {
    const ok = await submitOrder(showNotification);
    if (ok) goToStep(6);
  };

  // Pushes the order exactly like Push Order (re-pushing an initiated order is safe: totals are
  // recomputed and the "cart waiting" WhatsApp is not re-sent), then sends a Razorpay link for
  // the amount after cashback. Only the push's success toast is silenced.
  const handleGeneratePaymentLink = async () => {
    const link = await generateLink(
      orderId,
      () => submitOrder((message, type) => {
        if (type === 'error') showNotification(message, type);
      }),
      showNotification
    );
    if (link && step !== 6) goToStep(6);
  };

  // Resend never re-pushes the order: the backend re-texts the same link when the amount is unchanged.
  const handleResendPaymentLink = () => generateLink(orderId, null, showNotification);

  // contract: internalapi/views/user_type/shared/manual_service/diagnostic/cart/get.py (is_paid)
  // The order row stays `initiated` after an in-app, link or credit-line payment; only this says paid.
  const isOrderPaid = Boolean(cart?.is_paid);
  const paymentLinkDisabled = submitting || generatingLink || !cart?.final_payable_amount || isOrderPaid;

  /* ─── access guard ─────────────────────────────────────────────────────── */
  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-3">Access Denied</h2>
          <p className="text-gray-600">This feature is only available for Ops and SuperAdmin.</p>
          <button onClick={() => navigate('/subscriber-wallet')} className={`mt-4 px-4 py-2 rounded ${T.btn} text-sm`}>Go Home</button>
        </div>
      </div>
    );
  }

  /* ─── shared sub-elements ─────────────────────────────────────────────── */

  const Header = (
    <div className="flex items-center justify-between mb-8">
      {step < 6 ? (
        <button
          onClick={() => navigate(`/subscriber-wallet/subscriber/${id}`)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
        >
          ← Back to Subscriber
        </button>
      ) : <div className="w-40" />}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
          <FaFlask className="text-orange-500" size={14} />
        </div>
        <h1 className="text-xl font-semibold text-gray-900">Manual Diagnostic Order</h1>
      </div>
      <div className="w-40" />
    </div>
  );

  const Stepper = step < 6 && (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-3 mb-9 mt-5">
      <div className="flex items-center min-w-max ">
        {STEPS.map((label, idx) => {
          const n = idx + 1;
          const isDone = step > n;
          const isCurrent = step === n;
          const canJump = n < step;
          return (
            <React.Fragment key={label}>
              <button
                type="button"
                onClick={canJump ? () => goToStep(n) : undefined}
                disabled={!canJump}
                className={`flex items-center gap-2.5 focus:outline-none transition-all ${canJump ? 'cursor-pointer' : 'cursor-default'}`}
              >
                {/* circle */}
                <div className={`relative w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${isDone
                  ? 'bg-orange-500 text-white shadow-sm shadow-orange-200'
                  : isCurrent
                    ? 'bg-orange-500 text-white shadow-md shadow-orange-300 ring-4 ring-orange-100'
                    : 'bg-gray-100 text-gray-400'
                  }`}>
                  {isDone ? <FaCheck size={9} /> : n}
                  {isCurrent && <span className="absolute inset-0 rounded-full bg-orange-400 animate-ping opacity-30" />}
                </div>
                {/* label */}
                <span className={`text-xs font-medium whitespace-nowrap transition-all ${isCurrent ? 'text-orange-700 font-semibold' : isDone ? 'text-gray-500' : 'text-gray-300'
                  }`}>{label}</span>
              </button>
              {n < STEPS.length && (
                <div className={`mx-3 h-px flex-1 min-w-[24px] transition-all ${isDone ? 'bg-orange-300' : 'bg-gray-150 bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  const NavRow = ({ onBack, onNext, nextLabel = 'Next', nextDisabled = false, nextLoading = false }) => (
    <div className="flex justify-between mt-5 pt-4 border-t border-gray-100">
      {onBack ? (
        <button onClick={onBack} className="px-5 py-2 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50">
          Back
        </button>
      ) : <div />}
      <button
        onClick={onNext}
        disabled={nextDisabled || nextLoading}
        className={`px-6 py-2 rounded-md text-sm font-medium ${nextDisabled || nextLoading ? T.btnDis : T.btn}`}
      >
        {nextLoading ? 'Please wait…' : nextLabel}
      </button>
    </div>
  );


  /* ─── render ────────────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
      <style>{stepSlideStyles}</style>
      <div className="max-w-5xl mx-auto ">
        {Header}

        <>
          {Stepper}
          {/* step slide wrapper — key changes on every step transition, re-triggering the animation */}
          <div key={step} className={`${slideDir} overflow-hidden`}>

            {/* ── Step 1: Partner + Address ── */}
            {step === 1 && (
              <div className="rounded-2xl overflow-hidden shadow-sm border border-orange-100 bg-white flex flex-col h-[640px]">

                {/* Header */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50/60 px-6 py-4 border-b border-orange-100 shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center shadow-sm shadow-orange-200">
                        <FaFlask size={14} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-gray-800 font-semibold text-base leading-tight">Partner &amp; Address</h3>
                        <p className="text-gray-400 text-xs mt-0.5">Select a diagnostic partner and delivery address</p>
                      </div>
                    </div>
                    {selectedVendorId && selectedAddressId && (() => { const a = savedAddresses.find(x => x.id === selectedAddressId); return !a || (a.address_line_1 && a.pincode && a.sublocality && a.city && a.state); })() && (
                      <div className="flex items-center gap-1.5 bg-orange-500 rounded-full px-3 py-1 shrink-0">
                        <FaCheck size={9} className="text-white" />
                        <span className="text-white text-xs font-medium">Ready</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── two-column layout ── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 flex-1 min-h-0">

                  {/* ── LEFT: Address list + Check Serviceability ── */}
                  <div className="flex flex-col border-b lg:border-b-0 lg:border-r border-gray-100 min-h-0 overflow-hidden">

                    {/* panel header */}
                    <div className="px-5 pt-5 pb-4 flex items-center justify-between shrink-0">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center shadow-sm shadow-orange-200">
                          <svg viewBox="0 0 24 24" fill="none" className="w-3.5 h-3.5 text-white" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                            <circle cx="12" cy="9" r="2" fill="white" stroke="none" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800 leading-tight">Delivery Address</p>
                          <p className="text-xs text-gray-400 mt-0.5">{savedAddresses.length} saved</p>
                        </div>
                      </div>
                      <button
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200 transition-colors"
                        onClick={() => { setShowNewAddressForm(true); setSelectedAddressId(''); setServiceableVendors(null); setSelectedVendorId(''); }}
                      >
                        <FaPlus size={8} />
                        Add new
                      </button>
                    </div>

                    {/* address list — scrollable middle */}
                    <div className="flex-1 overflow-y-auto orange-scroll px-5 pt-1 pb-4 min-h-0">
                      {loadingAddresses ? (
                        <div className="space-y-2.5">
                          {[1, 2].map(i => (
                            <div key={i} className="h-[72px] bg-gradient-to-r from-gray-100 to-gray-50 animate-pulse rounded-2xl" />
                          ))}
                        </div>
                      ) : savedAddresses.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 rounded-2xl bg-gray-50 border border-dashed border-gray-200 gap-2.5">
                          <div className="w-11 h-11 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-gray-300" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                              <circle cx="12" cy="9" r="2" fill="currentColor" stroke="none" />
                            </svg>
                          </div>
                          <p className="text-sm font-medium text-gray-400">No saved addresses</p>
                          <p className="text-xs text-gray-300">Add one above to continue</p>
                        </div>
                      ) : (
                        <div className="space-y-2.5">
                          {savedAddresses.map(a => {
                            const isActive = selectedAddressId === a.id;
                            const ADDR_FIELD_LABELS = { address_line_1: 'Address Line 1', pincode: 'Pincode', sublocality: 'Sublocality', city: 'City', state: 'State' };
                            const missingFields = isActive
                              ? [
                                !a.address_line_1 && 'address_line_1',
                                !a.pincode && 'pincode',
                                !a.sublocality && 'sublocality',
                                !a.city && 'city',
                                !a.state && 'state',
                              ].filter(Boolean)
                              : [];
                            const hasAddrError = missingFields.length > 0;
                            return (
                              <div
                                key={a.id}
                                className={`rounded-2xl transition-all duration-200 overflow-hidden ${hasAddrError
                                  ? 'ring-2 ring-red-300 ring-offset-1 shadow-sm shadow-red-100'
                                  : isActive
                                    ? 'ring-2 ring-orange-400 ring-offset-1 shadow-md shadow-orange-100'
                                    : 'ring-1 ring-gray-200 hover:ring-orange-200 hover:shadow-sm'
                                  }`}
                              >
                                <button
                                  onClick={() => {
                                    if (selectedAddressId === a.id) return;
                                    setSelectedAddressId(a.id);
                                    setServiceableVendors(null);
                                    setSelectedVendorId('');
                                  }}
                                  className="w-full text-left"
                                >
                                  <div className={`px-4 py-3 ${hasAddrError ? 'bg-white' : isActive ? 'bg-gradient-to-br from-orange-50 to-amber-50/60' : 'bg-white'}`}>
                                    <div className="flex items-center gap-3">
                                      {/* left dot/icon */}
                                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors ${hasAddrError ? 'bg-red-100' : isActive ? 'bg-orange-500 shadow-sm shadow-orange-200' : 'bg-gray-100'}`}>
                                        <svg viewBox="0 0 24 24" fill="none" className={`w-3.5 h-3.5 ${hasAddrError ? 'text-red-400' : isActive ? 'text-white' : 'text-gray-400'}`} stroke="currentColor" strokeWidth={2.5}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                                          <circle cx="12" cy="9" r="2" fill="currentColor" stroke="none" />
                                        </svg>
                                      </div>

                                      {/* text */}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                          <p className={`text-sm font-semibold capitalize truncate ${hasAddrError ? 'text-gray-700' : isActive ? 'text-orange-800' : 'text-gray-800'}`}>
                                            {a.address_line_1 || <span className="text-red-400 italic font-normal">No address line</span>}
                                          </p>
                                          {a.address_type && (
                                            <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-orange-50 text-orange-500 border border-orange-100 capitalize">
                                              {a.address_type}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-400 truncate mt-0.5">
                                          {[a.sublocality, a.city].filter(Boolean).join(', ')}
                                          {a.pincode && <span className="ml-1.5 font-medium text-gray-500">{a.pincode}</span>}
                                        </p>
                                      </div>

                                      {/* actions */}
                                      <div className="flex items-center gap-1.5 shrink-0">
                                        <button
                                          type="button"
                                          onClick={e => { e.stopPropagation(); handleEditAddressClick(a); }}
                                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 text-gray-400 hover:bg-orange-100 hover:text-orange-500 transition-colors"
                                          title="Edit address"
                                        >
                                          <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                        </button>
                                        {hasAddrError
                                          ? <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center"><FaExclamationCircle className="text-red-500" size={9} /></div>
                                          : <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${isActive ? 'bg-orange-500' : 'bg-gray-100'}`}>
                                            {isActive ? <FaCheck className="text-white" size={7} /> : <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />}
                                          </div>
                                        }
                                      </div>
                                    </div>
                                  </div>
                                </button>
                                {hasAddrError && (
                                  <div className="px-4 py-2.5 bg-red-50 border-t border-red-100 flex items-center gap-2">
                                    <FaExclamationCircle className="text-red-400 shrink-0" size={9} />
                                    <p className="text-xs text-red-400">Missing: {missingFields.map(f => ADDR_FIELD_LABELS[f] || f).join(', ')}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                  </div>

                  {/* ── RIGHT: Serviceable Vendors ── */}
                  <div className="flex flex-col min-h-0 overflow-hidden">
                    {/* section label */}
                    <div className="px-5 pt-5 pb-3 shrink-0 flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center shadow-sm shadow-orange-200">
                        <FaFlask size={11} className="text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800 leading-tight">Diagnostic Partner</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {serviceableVendors === null
                            ? 'Check serviceability to see partners'
                            : serviceableVendors.length === 0
                              ? 'No partners available'
                              : `${serviceableVendors.length} partner${serviceableVendors.length > 1 ? 's' : ''} available`
                          }
                        </p>
                      </div>
                    </div>

                    {/* scrollable vendor area */}
                    <div className="flex-1 overflow-y-auto orange-scroll px-5 pt-1 pb-4 min-h-0">

                      {/* states: not checked / checking / empty / list */}
                      {serviceableVendors === null && !checkingServiceability && (
                        <div className="flex flex-col items-center justify-center h-full gap-4">
                          {/* animated icon */}
                          <div className="relative">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 flex items-center justify-center">
                              <FaFlask className="text-orange-300" size={26} />
                            </div>
                            {selectedAddressId && (
                              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                                <FaSearch size={8} className="text-white" />
                              </span>
                            )}
                          </div>
                          <div className="text-center space-y-1">
                            <p className="text-sm font-semibold text-gray-600">
                              {selectedAddressId ? 'Ready to find partners' : 'No address selected'}
                            </p>
                            <p className="text-xs text-gray-400 leading-relaxed">
                              {selectedAddressId
                                ? `Click "Check Serviceability" to see available diagnostic partners at ${members.find(m => (m.relation || '').toLowerCase() === 'self')?.name?.split(' ')[0] || 'user'}'s address`
                                : 'Pick a delivery address on the left — partners will load based on your location'
                              }
                            </p>
                          </div>
                          {selectedAddressId && (
                            <div className="flex items-center gap-1.5 text-xs text-orange-500 font-medium bg-orange-50 border border-orange-100 rounded-full px-3 py-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
                              Waiting for serviceability check
                            </div>
                          )}
                        </div>
                      )}

                      {checkingServiceability && (
                        <div className="flex flex-col items-center justify-center h-full gap-4">
                          <div className="relative">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 flex items-center justify-center">
                              <FaFlask className="text-orange-300" size={26} />
                            </div>
                            <div className="absolute -top-1 -right-1 w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                          </div>
                          <div className="text-center space-y-1">
                            <p className="text-sm font-semibold text-gray-600">Finding partners…</p>
                            <p className="text-xs text-gray-400">Checking which labs serve your area</p>
                          </div>
                        </div>
                      )}

                      {serviceableVendors !== null && !checkingServiceability && serviceableVendors.length === 0 && (
                        <div className="flex flex-col items-center justify-center h-full gap-3 rounded-2xl bg-red-50 border border-dashed border-red-200 py-10">
                          <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                            <FaExclamationCircle className="text-red-300" size={20} />
                          </div>
                          <p className="text-sm font-medium text-red-500">No partners serviceable</p>
                          <p className="text-xs text-red-400">Try a different address</p>
                        </div>
                      )}

                      {serviceableVendors !== null && !checkingServiceability && serviceableVendors.length > 0 && (
                        <div key={selectedAddressId} className="space-y-3" role="radiogroup" aria-label="Serviceable diagnostic partners">
                          {serviceableVendors.map((v, i) => (
                            <DiagnosticPartnerCard
                              key={v.id}
                              vendor={v}
                              index={i}
                              isActive={selectedVendorId === String(v.id)}
                              onSelect={() => {
                                setSelectedVendorId(String(v.id));
                                setIsAyushpayLab(v.is_ayushpay_lab ?? !!v.address);
                                if (v.home_collection && !v.walkin_collection) setCollectionType('home_collection');
                                else if (!v.home_collection && v.walkin_collection) setCollectionType('walkin');
                                else setCollectionType('');
                              }}
                            />
                          ))}
                        </div>
                      )}

                    </div>
                  </div>

                </div>{/* end two-column grid */}

                {/* ── Edit Address Modal ── */}
                {editingAddressId && createPortal(
                  (() => {
                    const EF = 'w-full bg-white border border-gray-200 rounded-2xl px-4 pt-8 pb-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all placeholder-gray-300';
                    const editFilled = [editAddr.address_line_1, editAddr.pincode, editAddr.sublocality, editAddr.city, editAddr.state].filter(Boolean).length;
                    const pct = Math.round((editFilled / 5) * 100);
                    return (
                      <div
                        className="macos-backdrop fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm"
                        onClick={() => { setEditingAddressId(null); setEditPincodeError(false); }}
                      >
                        <div
                          className="macos-open bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
                          onClick={e => e.stopPropagation()}
                        >
                          {/* drag handle (mobile) */}
                          <div className="flex justify-center pt-3 pb-1 sm:hidden">
                            <div className="w-10 h-1 rounded-full bg-gray-200" />
                          </div>

                          {/* header */}
                          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-xl bg-orange-100 flex items-center justify-center">
                                <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 text-orange-500" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                                  <circle cx="12" cy="9" r="2" fill="currentColor" stroke="none" />
                                </svg>
                              </div>
                              <p className="text-sm font-semibold text-gray-800">Edit Address</p>
                            </div>
                            <button
                              onClick={() => { setEditingAddressId(null); setEditPincodeError(false); }}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                              <FaTimes size={12} />
                            </button>
                          </div>

                          {/* progress bar */}
                          <div className="h-1 bg-gray-100 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-orange-400 to-amber-400 transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>

                          {/* form body — same layout as New Address form */}
                          <div className="px-5 py-4 space-y-3 max-h-[65vh] overflow-y-auto orange-scroll">

                            {/* pincode hero card */}
                            <div className={`rounded-2xl border bg-white overflow-hidden ${editPincodeError ? 'border-red-200' : 'border-gray-200'}`}>
                              <div className={`flex items-center gap-3 px-4 py-3 border-b ${editPincodeError ? 'border-red-100 bg-red-50' : 'border-gray-100'}`}>
                                <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors ${editPincodeError ? 'bg-red-100' : editAddr.pincode.length === 6 ? 'bg-orange-500' : 'bg-gray-100'}`}>
                                  <svg viewBox="0 0 24 24" fill="none" className={`w-4 h-4 ${editPincodeError ? 'text-red-400' : editAddr.pincode.length === 6 ? 'text-white' : 'text-gray-400'}`} stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                </div>
                                <div className="flex-1">
                                  <p className={`text-xs font-semibold uppercase tracking-wider ${editPincodeError ? 'text-red-500' : 'text-gray-400'}`}>Pincode <span className="text-orange-400">*</span></p>
                                  <p className={`text-xs font-normal normal-case ${editPincodeError ? 'text-red-400' : 'text-gray-400'}`}>{editPincodeError ? 'Invalid pincode — try again' : 'Enter 6-digit code to auto-fill city & state'}</p>
                                </div>
                                {editPincodeLoading && <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin shrink-0" />}
                                {!editPincodeLoading && editPincodeError && <FaExclamationCircle className="text-red-400 shrink-0" size={16} />}
                                {!editPincodeLoading && !editPincodeError && editAddr.pincode.length === 6 && <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center shrink-0"><FaCheck size={8} className="text-white" /></div>}
                              </div>
                              <div className="px-4 py-3">
                                <input
                                  type="text"
                                  maxLength={6}
                                  className={`w-full text-2xl font-bold tracking-[0.5em] focus:outline-none placeholder-gray-200 bg-transparent ${editPincodeError ? 'text-red-500' : 'text-gray-800'}`}
                                  placeholder="······"
                                  value={editAddr.pincode}
                                  onChange={async e => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setEditAddr(p => ({ ...p, pincode: val, city: '', state: '', sublocality: '' }));
                                    if (val.length < 6) { setEditPincodeError(false); setEditSublocalityOptions([]); return; }
                                    if (editPincodeAbortRef.current) editPincodeAbortRef.current.abort();
                                    const controller = new AbortController();
                                    editPincodeAbortRef.current = controller;
                                    setEditPincodeLoading(true); setEditPincodeError(false);
                                    try {
                                      const result = await lookupPincode(val);
                                      if (controller.signal.aborted) return; // a newer pincode is in flight
                                      setEditSublocalityOptions(result.sublocalityOptions || []);
                                      setEditAddr(p => ({ ...p, city: result.city || p.city, state: result.state || p.state, sublocality: result.sublocality || p.sublocality }));
                                    } catch (err) {
                                      if (!controller.signal.aborted) { setEditPincodeError(true); setEditSublocalityOptions([]); }
                                    }
                                    finally { if (!controller.signal.aborted) setEditPincodeLoading(false); }
                                  }}
                                />
                                {(editAddr.city || editAddr.state) && !editPincodeLoading && !editPincodeError && (
                                  <div className="flex items-center gap-1.5 mt-2">
                                    <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center"><FaCheck size={7} className="text-green-500" /></div>
                                    <p className="text-xs text-green-600 font-medium">{[editAddr.city, editAddr.state].filter(Boolean).join(', ')}</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* address line 1 */}
                            <div className="relative">
                              <label className="absolute left-4 top-3 text-xs font-semibold text-gray-400 uppercase tracking-wider pointer-events-none">Address Line 1 <span className="text-orange-400">*</span></label>
                              <input type="text" className={`${EF} pt-8 pb-3`} placeholder="Flat / House no., Building, Street" value={editAddr.address_line_1} onChange={e => setEditAddr(p => ({ ...p, address_line_1: e.target.value }))} />
                              {editAddr.address_line_1 && <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center"><FaCheck size={7} className="text-orange-500" /></div>}
                            </div>

                            {/* sublocality + landmark */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="relative">
                                <label className="absolute left-4 top-3 text-xs font-semibold text-gray-400 uppercase tracking-wider pointer-events-none">Sublocality <span className="text-orange-400">*</span></label>
                                {editAddr.pincode.length === 6 && editSublocalityOptions.length > 0 ? (
                                  <>
                                    <select className={`${EF} pt-8 pb-3 cursor-pointer`} value={editAddr.sublocality} onChange={e => setEditAddr(p => ({ ...p, sublocality: e.target.value }))}>
                                      {editAddr.sublocality && !editSublocalityOptions.includes(editAddr.sublocality) && <option value={editAddr.sublocality}>{editAddr.sublocality}</option>}
                                      {editSublocalityOptions.map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                    <FaChevronDown size={10} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
                                  </>
                                ) : (
                                  <>
                                    <input type="text" className={`${EF} pt-8 pb-3`} placeholder="Area / colony" value={editAddr.sublocality} onChange={e => setEditAddr(p => ({ ...p, sublocality: e.target.value }))} />
                                    {editAddr.sublocality && <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center"><FaCheck size={7} className="text-orange-500" /></div>}
                                  </>
                                )}
                              </div>
                              <div className="relative">
                                <label className="absolute left-4 top-3 text-xs font-semibold text-gray-400 uppercase tracking-wider pointer-events-none">Landmark</label>
                                <input type="text" className={`${EF} pt-8 pb-3`} placeholder="Near school / hospital…" value={editAddr.landmark} onChange={e => setEditAddr(p => ({ ...p, landmark: e.target.value }))} />
                              </div>
                            </div>

                            {/* city + state */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="relative">
                                <label className="absolute left-4 top-3 text-xs font-semibold text-gray-400 uppercase tracking-wider pointer-events-none">City <span className="text-orange-400">*</span></label>
                                <input type="text" className={`${EF} pt-8 pb-3`} placeholder="City" value={editAddr.city} onChange={e => setEditAddr(p => ({ ...p, city: e.target.value }))} />
                                {editAddr.city && <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center"><FaCheck size={7} className="text-orange-500" /></div>}
                              </div>
                              <div className="relative">
                                <label className="absolute left-4 top-3 text-xs font-semibold text-gray-400 uppercase tracking-wider pointer-events-none">State <span className="text-orange-400">*</span></label>
                                <input type="text" className={`${EF} pt-8 pb-3`} placeholder="State" value={editAddr.state} onChange={e => setEditAddr(p => ({ ...p, state: e.target.value }))} />
                                {editAddr.state && <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center"><FaCheck size={7} className="text-orange-500" /></div>}
                              </div>
                            </div>

                            {/* location: auto-fetch + draggable map + editable lat/long */}
                            <DiagnosticGeoFields
                              role={currentRole}
                              address={editAddr}
                              latitude={editAddr.latitude}
                              longitude={editAddr.longitude}
                              onChange={patch => setEditAddr(p => ({ ...p, ...patch }))}
                              onNotify={showNotification}
                            />

                            {/* address type */}
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Address Type</p>
                              <div className="flex gap-2">
                                {['home', 'office', 'other'].map(t => (
                                  <button
                                    key={t}
                                    type="button"
                                    onClick={() => setEditAddr(p => ({ ...p, address_type: t }))}
                                    className={`flex-1 py-2 rounded-xl text-xs font-semibold border capitalize transition-all ${editAddr.address_type === t ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-500 border-gray-200 hover:border-orange-300'}`}
                                  >
                                    {t}
                                  </button>
                                ))}
                              </div>
                              {editAddr.address_type === 'other' && (
                                <input
                                  type="text"
                                  className="mt-2 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 placeholder-gray-300"
                                  placeholder="Describe address type…"
                                  value={editAddr.address_type_custom || ''}
                                  onChange={e => setEditAddr(p => ({ ...p, address_type_custom: e.target.value }))}
                                />
                              )}
                            </div>

                            {/* save CTA */}
                            <button
                              onClick={handleUpdateAddress}
                              disabled={updatingAddress || editFilled < 5 || editAddr.pincode.length < 6 || editPincodeError || Boolean(latError(editAddr.latitude)) || Boolean(lngError(editAddr.longitude))}
                              className="w-full py-3.5 rounded-2xl text-sm font-bold bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-orange-200 flex items-center justify-center gap-2"
                            >
                              {updatingAddress
                                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
                                : <><FaCheck size={11} /> Save Changes</>
                              }
                            </button>

                          </div>
                        </div>
                      </div>
                    );
                  })(),
                  document.body
                )}

                {/* ── New Address Modal ── */}
                {showNewAddressForm && createPortal(
                  (() => {
                    const NF = 'w-full bg-white border border-gray-200 rounded-2xl px-4 pt-8 pb-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all placeholder-gray-300';
                    const filled = [addr.address_line_1, addr.pincode, addr.sublocality, addr.city, addr.state].filter(Boolean).length;
                    const total = 5;
                    const pct = Math.round((filled / total) * 100);
                    return (
                      <div
                        className="macos-backdrop fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm"
                        onClick={() => { setShowNewAddressForm(false); setPincodeError(false); setAddr({ address_line_1: '', city: '', state: '', pincode: '', landmark: '', sublocality: '', address_type: 'home', latitude: '', longitude: '' }); }}
                      >
                        <div
                          className="macos-open bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
                          onClick={e => e.stopPropagation()}
                        >
                          {/* drag handle (mobile) */}
                          <div className="flex justify-center pt-3 pb-1 sm:hidden">
                            <div className="w-10 h-1 rounded-full bg-gray-200" />
                          </div>

                          {/* header */}
                          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center shadow-sm shadow-orange-200">
                                <FaPlus size={10} className="text-white" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-800">New Delivery Address</p>
                                <p className="text-xs text-gray-400">{filled} of {total} fields filled</p>
                              </div>
                            </div>
                            <button
                              onClick={() => { setShowNewAddressForm(false); setPincodeError(false); setAddr({ address_line_1: '', city: '', state: '', pincode: '', landmark: '', sublocality: '', address_type: 'home', latitude: '', longitude: '' }); }}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                              <FaTimes size={12} />
                            </button>
                          </div>

                          {/* progress bar */}
                          <div className="h-1 bg-gray-100 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-orange-400 to-amber-400 transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>

                          {/* form body */}
                          <div className="px-5 py-4 space-y-3 max-h-[65vh] overflow-y-auto orange-scroll">

                            {/* pincode hero card */}
                            <div className={`rounded-2xl border bg-white overflow-hidden ${pincodeError ? 'border-red-200' : 'border-gray-200'}`}>
                              <div className={`flex items-center gap-3 px-4 py-3 border-b ${pincodeError ? 'border-red-100 bg-red-50' : 'border-gray-100'}`}>
                                <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors ${pincodeError ? 'bg-red-100' : addr.pincode.length === 6 ? 'bg-orange-500' : 'bg-gray-100'}`}>
                                  <svg viewBox="0 0 24 24" fill="none" className={`w-4 h-4 ${pincodeError ? 'text-red-400' : addr.pincode.length === 6 ? 'text-white' : 'text-gray-400'}`} stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                </div>
                                <div className="flex-1">
                                  <p className={`text-xs font-semibold uppercase tracking-wider ${pincodeError ? 'text-red-500' : 'text-gray-400'}`}>Pincode <span className="text-orange-400">*</span></p>
                                  <p className={`text-xs font-normal normal-case ${pincodeError ? 'text-red-400' : 'text-gray-400'}`}>{pincodeError ? 'Invalid pincode — try again' : 'Auto-fills city & state'}</p>
                                </div>
                                {pincodeLoading && <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin shrink-0" />}
                                {!pincodeLoading && pincodeError && <FaExclamationCircle className="text-red-400 shrink-0" size={16} />}
                                {!pincodeLoading && !pincodeError && addr.pincode.length === 6 && <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center shrink-0"><FaCheck size={8} className="text-white" /></div>}
                              </div>
                              <div className="px-4 py-3">
                                <input type="text" maxLength={6} className={`w-full text-2xl font-bold tracking-[0.5em] focus:outline-none placeholder-gray-200 bg-transparent ${pincodeError ? 'text-red-500' : 'text-gray-800'}`} placeholder="······" value={addr.pincode} onChange={e => { const val = e.target.value.replace(/\D/g, ''); setAddr(p => ({ ...p, pincode: val, city: '', state: '', sublocality: '' })); fetchPincodeData(val); }} />
                                {(addr.city || addr.state) && !pincodeLoading && !pincodeError && (
                                  <div className="flex items-center gap-1.5 mt-2">
                                    <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center"><FaCheck size={7} className="text-green-500" /></div>
                                    <p className="text-xs text-green-600 font-medium">{[addr.city, addr.state].filter(Boolean).join(', ')}</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* address line 1 */}
                            <div className="relative">
                              <label className="absolute left-4 top-3 text-xs font-semibold text-gray-400 uppercase tracking-wider pointer-events-none">Address Line 1 <span className="text-orange-400">*</span></label>
                              <input type="text" className={`${NF}`} placeholder="Flat / House no., Building, Street" value={addr.address_line_1} onChange={e => setAddr(p => ({ ...p, address_line_1: e.target.value }))} />
                              {addr.address_line_1 && <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center"><FaCheck size={7} className="text-orange-500" /></div>}
                            </div>

                            {/* sublocality + landmark */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="relative">
                                <label className="absolute left-4 top-3 text-xs font-semibold text-gray-400 uppercase tracking-wider pointer-events-none">Sublocality <span className="text-orange-400">*</span></label>
                                {addr.pincode.length === 6 && sublocalityOptions.length > 0 ? (
                                  <>
                                    <select className={`${NF} cursor-pointer`} value={addr.sublocality} onChange={e => setAddr(p => ({ ...p, sublocality: e.target.value }))}>
                                      {addr.sublocality && !sublocalityOptions.includes(addr.sublocality) && <option value={addr.sublocality}>{addr.sublocality}</option>}
                                      {sublocalityOptions.map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                    <FaChevronDown size={10} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
                                  </>
                                ) : (
                                  <>
                                    <input type="text" className={`${NF}`} placeholder="Area / colony" value={addr.sublocality} onChange={e => setAddr(p => ({ ...p, sublocality: e.target.value }))} />
                                    {addr.sublocality && <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center"><FaCheck size={7} className="text-orange-500" /></div>}
                                  </>
                                )}
                              </div>
                              <div className="relative">
                                <label className="absolute left-4 top-3 text-xs font-semibold text-gray-400 uppercase tracking-wider pointer-events-none">Landmark</label>
                                <input type="text" className={`${NF}`} placeholder="Near school / hospital…" value={addr.landmark} onChange={e => setAddr(p => ({ ...p, landmark: e.target.value }))} />
                              </div>
                            </div>

                            {/* city + state */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="relative">
                                <label className="absolute left-4 top-3 text-xs font-semibold text-gray-400 uppercase tracking-wider pointer-events-none">City <span className="text-orange-400">*</span></label>
                                <input type="text" className={`${NF}`} placeholder="City" value={addr.city} onChange={e => setAddr(p => ({ ...p, city: e.target.value }))} />
                                {addr.city && <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center"><FaCheck size={7} className="text-orange-500" /></div>}
                              </div>
                              <div className="relative">
                                <label className="absolute left-4 top-3 text-xs font-semibold text-gray-400 uppercase tracking-wider pointer-events-none">State <span className="text-orange-400">*</span></label>
                                <input type="text" className={`${NF}`} placeholder="State" value={addr.state} onChange={e => setAddr(p => ({ ...p, state: e.target.value }))} />
                                {addr.state && <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center"><FaCheck size={7} className="text-orange-500" /></div>}
                              </div>
                            </div>

                            {/* location: auto-fetch + draggable map + editable lat/long */}
                            <DiagnosticGeoFields
                              role={currentRole}
                              address={addr}
                              latitude={addr.latitude}
                              longitude={addr.longitude}
                              onChange={patch => setAddr(p => ({ ...p, ...patch }))}
                              onNotify={showNotification}
                            />

                            {/* address type */}
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Address Type <span className="text-orange-400">*</span></p>
                              <div className="flex gap-2">
                                {['home', 'office', 'other'].map(t => (
                                  <button
                                    key={t}
                                    type="button"
                                    onClick={() => setAddr(p => ({ ...p, address_type: t }))}
                                    className={`flex-1 py-2 rounded-xl text-xs font-semibold border capitalize transition-all ${addr.address_type === t ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-500 border-gray-200 hover:border-orange-300'}`}
                                  >
                                    {t}
                                  </button>
                                ))}
                              </div>
                              {addr.address_type === 'other' && (
                                <input
                                  type="text"
                                  className="mt-2 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 placeholder-gray-300"
                                  placeholder="Describe address type…"
                                  value={addr.address_type_custom || ''}
                                  onChange={e => setAddr(p => ({ ...p, address_type_custom: e.target.value }))}
                                />
                              )}
                            </div>

                            {/* save CTA */}
                            <button
                              onClick={handleSaveAddress}
                              disabled={savingAddress || filled < total || addr.pincode.length < 6 || pincodeError || Boolean(latError(addr.latitude)) || Boolean(lngError(addr.longitude))}
                              className="w-full py-3.5 rounded-2xl text-sm font-bold bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-orange-200 flex items-center justify-center gap-2"
                            >
                              {savingAddress
                                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving address…</>
                                : <><FaCheck size={11} /> Save &amp; Use This Address</>
                              }
                            </button>

                          </div>
                        </div>
                      </div>
                    );
                  })(),
                  document.body
                )}

                <div className="px-5 py-4 bg-white border-t border-gray-100 shrink-0">
                  {/* step indicator */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${selectedAddressId ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-400'}`}>1</div>
                    <div className={`h-px flex-1 ${serviceableVendors !== null ? 'bg-orange-300' : 'bg-gray-200'}`} />
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${serviceableVendors !== null ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-400'}`}>2</div>
                    <div className={`h-px flex-1 ${selectedVendorId ? 'bg-orange-300' : 'bg-gray-200'}`} />
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${selectedVendorId ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-400'}`}>3</div>
                  </div>

                  {/* action row */}
                  <div className="flex items-center gap-3">
                    {/* Check Serviceability — disabled until address selected with all fields filled */}
                    {(() => {
                      const selAddr = savedAddresses.find(a => a.id === selectedAddressId);
                      const addrComplete = selAddr && selAddr.address_line_1 && selAddr.pincode && selAddr.sublocality && selAddr.city && selAddr.state;
                      return (
                        <button
                          type="button"
                          onClick={handleCheckServiceability}
                          disabled={checkingServiceability || !addrComplete}
                          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed ${serviceableVendors !== null && addrComplete
                            ? 'bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100'
                            : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-sm shadow-orange-200'
                            }`}
                        >
                          {checkingServiceability
                            ? <><span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> Checking…</>
                            : serviceableVendors !== null && addrComplete
                              ? <><FaSearch size={10} /> Re-check</>
                              : <><FaSearch size={10} /> Check Serviceability</>
                          }
                        </button>
                      );
                    })()}

                    {/* Create Order — primary only after vendor selected */}
                    <button
                      onClick={handleCreateOrder}
                      disabled={!selectedVendorId || !selectedAddressId || creatingOrder || (() => { const a = savedAddresses.find(x => x.id === selectedAddressId); return a && (!a.address_line_1 || !a.pincode || !a.sublocality || !a.city || !a.state); })()}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.99] ${!selectedVendorId || !selectedAddressId || creatingOrder
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-sm shadow-orange-200'
                        }`}
                    >
                      {creatingOrder
                        ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Creating…</>
                        : <>Create Order <span className="opacity-70">→</span></>
                      }
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 2: Select Patients ── */}
            {step === 2 && (
              <div className="rounded-2xl overflow-hidden shadow-sm border border-orange-100 flex flex-col h-[590px]">

                {/* Header */}
                <div className="bg-orange-50 px-5 py-4 border-b border-orange-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-gray-800 font-semibold text-base leading-tight">Select Patients</h3>
                      <p className="text-gray-400 text-xs mt-0.5">Choose members for this diagnostic order</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedMemberIds.length > 0 && (
                        <div className="flex items-center gap-1.5 bg-orange-500 rounded-full px-3 py-1 shrink-0">
                          <FaCheck size={9} className="text-white" />
                          <span className="text-white text-xs font-medium">{selectedMemberIds.length} selected</span>
                        </div>
                      )}
                      {members.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowAddModal(true)}
                          disabled={addingMember}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${addingMember ? 'text-gray-400 border-gray-200 cursor-not-allowed bg-white' : 'text-orange-600 border-orange-300 hover:bg-orange-100 bg-white'}`}
                        >
                          <FaUserPlus size={10} />
                          {addingMember ? 'Adding…' : 'Add Member'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-white px-5 pt-5 pb-4 space-y-3 flex-1 overflow-y-auto orange-scroll">
                  {loadingPatients || savingPatients ? (
                    <div className="flex justify-center py-10">
                      <div className={`animate-spin rounded-full h-10 w-10 border-b-2 ${T.spinner}`} />
                    </div>
                  ) : members.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
                      <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center">
                        <FaUserPlus className="text-orange-300" size={20} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">No members found</p>
                        <p className="text-xs text-gray-400 mt-0.5">Add a member to get started</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddModal(true)}
                        disabled={addingMember}
                        className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl ${T.btn}`}
                      >
                        <FaUserPlus size={12} />
                        {addingMember ? 'Adding…' : 'Add Member'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {members.map(m => {
                        const isSelected = selectedMemberIds.includes(m.id);
                        const isSelf = (m.relation || '').toLowerCase() === 'self';
                        const missingFields = [
                          !m.name && 'name',
                          !(m.dob || m.date_of_birth) && 'dob',
                          !m.relation && 'relation',
                          !(m.contact_number || m.phone || m.mobile) && 'phone',
                          !m.gender && 'gender',
                          isSelf && !m.email && 'email',
                        ].filter(Boolean);
                        const FIELD_LABELS = { name: 'Name', dob: 'Date of birth', relation: 'Relation', phone: 'Phone', gender: 'Gender', email: 'Email' };
                        const hasMissing = missingFields.length > 0;
                        const age = calculateAgeFromDob(m.dob || m.date_of_birth);
                        const isTooYoung = isSelected && !hasMissing && age !== null && age < 5;
                        const hasError = hasMissing || isTooYoung;
                        return (
                          <div key={m.id} className={`rounded-2xl border transition-all duration-200 overflow-hidden ${hasError
                            ? 'border-red-200 shadow-sm shadow-red-100'
                            : isSelected
                              ? 'border-orange-300 shadow-sm shadow-orange-100'
                              : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
                            }`}>
                            <div className={`flex items-center gap-3 px-4 py-3.5 ${hasError ? 'bg-white' : isSelected ? 'bg-gradient-to-r from-orange-50 to-amber-50/40' : 'bg-white'
                              }`}>
                              <button
                                onClick={() => toggleMember(m.id)}
                                className={`w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center transition-all duration-150 ${isSelected
                                  ? hasError ? 'bg-red-400 border-red-400' : 'bg-orange-500 border-orange-500'
                                  : 'bg-white border-gray-300 hover:border-orange-400'
                                  }`}
                              >
                                {isSelected && <FaCheck className="text-white" size={8} />}
                              </button>
                              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleMember(m.id)}>
                                <p className="text-sm font-medium text-gray-800">{m.name}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{[m.relation, age != null ? `${age} yrs` : null, m.gender].filter(Boolean).join(' · ') || 'No details'}</p>
                              </div>
                              <button
                                onClick={e => { e.stopPropagation(); setEditingMember(m); }}
                                className="shrink-0 px-3 py-1 text-xs text-gray-400 rounded-lg bg-gray-50 hover:bg-orange-50 hover:text-orange-500 transition-all"
                              >
                                Edit
                              </button>
                              {isSelected && !hasError && (
                                <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center shrink-0">
                                  <FaCheck className="text-white" size={8} />
                                </div>
                              )}
                              {hasError && (
                                <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                                  <FaExclamationCircle className="text-red-500" size={10} />
                                </div>
                              )}
                            </div>
                            {hasMissing && (
                              <div className="px-4 py-3 bg-red-50 border-t border-red-100 flex items-center gap-3">
                                <p className="text-xs text-red-500 font-medium shrink-0">Missing:</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {missingFields.map(f => (
                                    <span key={f} className="px-2 py-0.5 rounded-full text-xs font-medium bg-white text-red-500 border border-red-200 shadow-sm">
                                      {FIELD_LABELS[f] || f}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {isTooYoung && (
                              <div className="px-4 py-3 bg-red-50 border-t border-red-100">
                                <p className="text-xs text-red-500 font-medium">Age must be at least 5 years for diagnostic tests</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 bg-white border-t border-gray-100">
                  <NavRow
                    onBack={() => goToStep(1)}
                    onNext={handleSavePatients}
                    nextLabel="Save & Continue"
                    nextDisabled={selectedMemberIds.length === 0}
                    nextLoading={savingPatients}
                  />
                </div>
              </div>
            )}

            <AddFamilyMemberModal
              mounted={true}
              show={showAddModal}
              apiErrors={addApiErrors}
              onClose={() => { setShowAddModal(false); setAddApiErrors({}); }}
              onSubmit={async (memberData) => {
                setAddingMember(true);
                setAddApiErrors({});
                try {
                  const res = await addFamilyMember(id, currentRole, {
                    name: memberData.name,
                    relation: formatRelationshipForApi(memberData.relationship),
                    gender: formatGenderForApi(memberData.gender),
                    age: memberData.dob ? calculateAgeFromDob(memberData.dob) : memberData.age,
                    dob: memberData.dob,
                    contact_number: memberData.mobile,
                    email: memberData.email,
                  });
                  if (res?.metadata?.api_status === true) {
                    setShowAddModal(false);
                    setAddApiErrors({});
                    showNotification('Member added successfully', 'success');
                    const newMemberId = res?.msg?.id;
                    if (orderId) await loadPatients(orderId, showNotification, newMemberId, true);
                    return true;
                  } else {
                    const details = res?.msg?.details;
                    const errMsg = res?.msg?.error;
                    if (details && typeof details === 'object') {
                      setAddApiErrors(details);
                    } else if (errMsg) {
                      showNotification(String(errMsg), 'error');
                    } else {
                      showNotification('Failed to add member', 'error');
                    }
                    return false;
                  }
                } catch (err) {
                  try {
                    const match = err.message.match(/:\s*(\{.*\})\s*$/s);
                    if (match) {
                      const parsed = JSON.parse(match[1]);
                      const details = parsed?.msg?.details;
                      const errMsg = parsed?.msg?.error;
                      if (details && typeof details === 'object') {
                        setAddApiErrors(details);
                        return false;
                      } else if (errMsg) {
                        showNotification(String(errMsg), 'error');
                        return false;
                      }
                    }
                  } catch { /* fall through */ }
                  showNotification(err.message || 'Failed to add member', 'error');
                  return false;
                } finally {
                  setAddingMember(false);
                }
              }}
              existingSingletonRelations={new Set(members.filter(m => ['self', 'spouse'].includes((m.relation || '').toLowerCase())).map(m => (m.relation || '').toLowerCase()))}
              existingMembers={members}
            />

            <AddFamilyMemberModal
              mounted={true}
              show={!!editingMember}
              initialData={editingMember}
              apiErrors={editApiErrors}
              onClose={() => { setEditingMember(null); setEditApiErrors({}); }}
              onSubmit={handleEditMember}
              existingSingletonRelations={new Set()}
              existingMembers={members}
            />

            {/* ── Step 3: Add Tests ── */}
            {step === 3 && (() => {
              const activeMember = localMembers.find(m => m.id === activeMemberId);
              const products = activeMember?.products || [];
              const subtotal = products.reduce((s, p) => s + (Number(p.mrp) || 0), 0);
              const totalTests = localMembers.reduce((n, m) => n + (m.products || []).length, 0);
              return (
                <div className="rounded-2xl overflow-hidden shadow-sm border border-orange-100">

                  {/* ── header ── */}
                  <div className="bg-orange-50 px-5 py-4 border-b border-orange-100">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-gray-800 font-semibold text-base leading-tight">Add Diagnostic Tests</h3>
                        <p className="text-gray-400 text-xs mt-0.5">Select patient · search · add</p>
                      </div>
                      {totalTests > 0 && (
                        <div className="flex items-center gap-1.5 bg-orange-500 rounded-full px-3 py-1">
                          <FaFlask size={9} className="text-white" />
                          <span className="text-white text-xs font-medium">{totalTests} test{totalTests !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>

                    {/* Patient selector */}
                    <div className="flex gap-2 overflow-x-auto pb-0.5">
                      {localMembers.map(m => {
                        const cnt = (m.products || []).length;
                        const isActive = activeMemberId === m.id;
                        const noTests = cnt === 0;
                        return (
                          <button
                            key={m.id}
                            onClick={() => setActiveMemberId(m.id)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium shrink-0 transition-all border ${isActive
                              ? noTests ? 'bg-red-500 text-white border-red-500 shadow-sm' : 'bg-orange-500 text-white border-orange-500 shadow-sm'
                              : noTests ? 'bg-white text-red-500 border-red-200 hover:border-red-300' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                              }`}
                          >
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${isActive ? 'bg-white text-orange-500' : 'bg-gray-100 text-gray-500'}`}>
                              {m.name.charAt(0).toUpperCase()}
                            </span>
                            {m.name}
                            {noTests
                              ? <span className={`w-4 h-4 flex items-center justify-center rounded-full text-xs font-bold ${isActive ? 'bg-white text-red-500' : 'bg-red-100 text-red-500'}`}>!</span>
                              : <span className={`w-4 h-4 flex items-center justify-center rounded-full text-xs font-medium ${isActive ? 'bg-white text-orange-500' : 'bg-orange-100 text-orange-600'}`}>{cnt}</span>
                            }
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── panels ── */}
                  <div className="flex bg-white gap-px" style={{ height: '440px' }}>

                    <div className="flex flex-col w-1/2">

                      <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center text-white text-sm font-medium shadow-sm">
                            {activeMember?.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800 leading-tight">{activeMember?.name}</p>
                            <p className="text-xs text-gray-400 font-medium">{[activeMember?.relation, activeMember?.age ? `${activeMember.age} yrs` : null].filter(Boolean).join(' · ') || 'Patient'}</p>
                          </div>
                        </div>
                        {products.length > 0 && (
                          <div className="text-right">
                            <p className="text-sm font-semibold text-orange-600">{fmt(subtotal)}</p>
                            <p className="text-xs text-gray-400">{products.length} test{products.length !== 1 ? 's' : ''}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex-1 overflow-y-auto orange-scroll">
                        {products.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full gap-3 px-8 text-center">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center shadow-inner">
                              <FaFlask className="text-orange-200" size={20} />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-400">No tests added</p>
                              <p className="text-xs text-gray-300 mt-0.5">Search and add from the left</p>
                            </div>
                          </div>
                        ) : products.map((p, i) => (
                          <div key={i} className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-50 hover:bg-gray-50 transition-colors group">
                            <span className="w-6 h-6 rounded-lg bg-orange-100 flex items-center justify-center text-xs font-medium text-orange-500 shrink-0">{i + 1}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 leading-snug">{p.name}</p>
                              <div className="flex items-center gap-2 mt-2.5">
                                {!isAyushpayLab && <span className="text-xs text-gray-400 line-through">{fmt(p.cp)}</span>}
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-orange-100 text-orange-600 text-[13px]  font-semibold">{fmt(p.mrp)}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveTest(activeMemberId, p.product_id || p.name)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all shrink-0"
                            >
                              <FaTrash size={10} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Left — catalog search */}
                    <div className="flex flex-col w-1/2" style={{ borderLeft: '1.5px solid #f0f0f0' }}>

                      <div className="px-4 py-3" style={{ background: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-2">Catalog</p>
                        <div className="relative">
                          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={11} />
                          <input
                            type="text"
                            className="w-full pl-8 pr-8 py-2.5 rounded-xl text-sm border border-gray-200 bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                            placeholder={`Search for ${activeMember?.name || 'patient'}…`}
                            value={testQuery}
                            onChange={e => {
                              const val = e.target.value;
                              setTestQuery(val);
                              clearTimeout(searchDebounceRef.current);
                              searchDebounceRef.current = setTimeout(() => searchTests(orderId, val), 400);
                            }}
                          />
                          {searching
                            ? <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                            : testQuery && <FaTimes className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 cursor-pointer hover:text-gray-500" size={11} onClick={() => { clearTimeout(searchDebounceRef.current); setTestQuery(''); setSearchResults([]); }} />
                          }
                        </div>
                      </div>

                      <div className="flex-1 overflow-y-auto orange-scroll">
                        {searchResults.length > 0 ? (
                          <>
                            <div className="px-4 py-2 bg-orange-50 border-b border-orange-100">
                              <span className="text-xs font-semibold text-orange-500">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</span>
                            </div>
                            {searchResults.map(item => {
                              const pid = item.product_id;
                              const isExpanded = expandedTestId === pid;
                              const detail = testDetailCache[pid];
                              const canExpand = !!pid;
                              const added = (activeMember?.products || []).some(
                                p => (p.product_id && pid && p.product_id === pid) || p.name === item.name
                              );
                              return (
                                <div key={pid || item.name} className="border-b border-gray-50">
                                  <div className="flex items-center gap-3 px-4 py-3.5 hover:bg-orange-50/50 transition-colors group cursor-default">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-800 leading-snug">{item.name}</p>
                                      <div className="flex items-center gap-2 mt-2.5">
                                        {isAyushpayLab ? (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-orange-100 text-orange-600 text-[13px] font-semibold">{fmt(item.mrp)}</span>
                                        ) : (
                                          <>
                                            <span className="text-xs text-gray-400 line-through">{fmt(item.mrp)}</span>
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-orange-100 text-orange-600 text-[13px] font-semibold">{fmt(item.price)}</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    {canExpand && (
                                      <button
                                        onClick={() => handleToggleTestDetail(pid)}
                                        className="text-gray-400 hover:text-orange-500 transition-colors shrink-0 p-1"
                                        title="View test details"
                                      >
                                        {isExpanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                                      </button>
                                    )}
                                    {added ? (
                                      <span
                                        aria-disabled="true"
                                        title="Already added"
                                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-orange-50 text-orange-500 ring-1 ring-orange-200 text-xs font-medium shrink-0 cursor-not-allowed select-none"
                                      >
                                        <FaCheck size={8} /> Added
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => handleAddTest(activeMemberId, item)}
                                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-medium hover:bg-orange-600 shrink-0 shadow hover:shadow-md transition-all active:scale-95"
                                      >
                                        <FaPlus size={8} /> Add
                                      </button>
                                    )}
                                  </div>
                                  {isExpanded && (
                                    <div className="px-4 pb-3 bg-orange-50/40 border-t border-orange-100">
                                      {(!detail || testDetailLoading) ? (
                                        <p className="text-xs text-gray-400 py-2">Loading details...</p>
                                      ) : !detail.ok ? (
                                        <p className="text-xs text-red-400 py-2">Unable to load details.</p>
                                      ) : (
                                        <div className="py-2 space-y-2">
                                          {detail.data.description && (
                                            <p className="text-xs text-gray-600 leading-relaxed">{detail.data.description}</p>
                                          )}
                                          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                                            {detail.data.fasting_required && (
                                              <span><span className="font-medium text-gray-700">Fasting:</span> {capFirst(detail.data.fasting_required)}{detail.data.fasting_time ? ` (${detail.data.fasting_time})` : ''}</span>
                                            )}
                                            {detail.data.report_time && (
                                              <span><span className="font-medium text-gray-700">Report:</span> {detail.data.report_time} hr</span>
                                            )}
                                            {detail.data.gender && (
                                              <span><span className="font-medium text-gray-700">Gender:</span> {detail.data.gender}</span>
                                            )}
                                          </div>
                                          {Array.isArray(detail.data.constituents) && detail.data.constituents.length > 0 && (
                                            <div>
                                              <p className="text-xs font-medium text-gray-600 mb-1">Includes ({detail.data.constituents.length} tests):</p>
                                              <div className="flex flex-wrap gap-1">
                                                {detail.data.constituents.map((c, i) => (
                                                  <span key={i} className="text-[11px] bg-white border border-orange-100 text-gray-600 px-2 py-0.5 rounded-full">{c.name || c}</span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full gap-3 px-8 text-center">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shadow-inner">
                              <FaSearch className="text-gray-300" size={20} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-400">{testQuery && !searching ? 'No results found' : 'Search tests'}</p>
                              <p className="text-xs text-gray-300 mt-0.5">{testQuery && !searching ? 'Try a different name' : 'Type above to search the catalog'}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right — selected tests */}

                  </div>

                  {/* ── collection type (AyushPay labs only; partners keep their own journey) ── */}
                  {isAyushpayLab && (() => {
                    const _vendor = (serviceableVendors || []).find(sv => String(sv.id) === String(selectedVendorId));
                    const ALL_OPTS = [
                      { key: 'home_collection', title: 'Home Collection', desc: 'Sample picked up at address', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                      { key: 'walkin', title: 'Walk-in at Centre', desc: 'Patient visits the lab', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m4-14h.01M11 7h.01M7 11h.01M11 11h.01M7 15h.01M11 15h.01' },
                    ];
                    const opts = _vendor
                      ? ALL_OPTS.filter(o => o.key === 'home_collection' ? _vendor.home_collection : _vendor.walkin_collection)
                      : ALL_OPTS;
                    const isSingle = opts.length === 1;
                    return (
                      <div className="px-6 pt-4 pb-1 bg-white border-t border-gray-100">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">
                          Collection Type {!isSingle && <span className="text-red-400">*</span>}
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {opts.map(opt => {
                            const active = collectionType === opt.key;
                            return (
                              <button
                                key={opt.key}
                                type="button"
                                onClick={() => setCollectionType(opt.key)}
                                className={`flex items-start gap-3 text-left px-4 py-3 rounded-2xl border transition-all active:scale-[0.99] ${active
                                  ? 'border-orange-500 bg-orange-50 shadow-sm'
                                  : 'border-gray-200 bg-white hover:border-orange-300'
                                  } ${isSingle ? 'cursor-default' : ''}`}
                              >
                                <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${active ? 'bg-orange-500' : 'bg-gray-100'}`}>
                                  <svg className={`w-4 h-4 ${active ? 'text-white' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d={opt.icon} /></svg>
                                </span>
                                <span className="min-w-0">
                                  <span className={`block text-sm font-semibold leading-tight ${active ? 'text-orange-700' : 'text-gray-700'}`}>{opt.title}</span>
                                  <span className="block text-xs text-gray-400 mt-0.5">{opt.desc}</span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── footer ── */}
                  <div className="px-6 py-3.5 bg-white">
                    <NavRow
                      onBack={() => goToStep(2)}
                      onNext={handleGoToSlots}
                      nextLabel={savingCollection ? 'Saving…' : 'Next: Choose Slot →'}
                      nextDisabled={savingCollection}
                    />
                  </div>
                </div>
              );
            })()}

            {/* ── Step 4: Slot ── */}
            {step === 4 && (() => {
              return (
                <div className="rounded-2xl overflow-hidden shadow-sm border border-orange-100">

                  {/* Header */}
                  <div className="bg-orange-50 px-5 py-4 border-b border-orange-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-gray-800 font-semibold text-base leading-tight">Choose Collection Slot</h3>
                        <p className="text-gray-400 text-xs mt-0.5">Pick a date and a convenient time window</p>
                      </div>
                      {selectedSlot && (
                        <div className="flex items-center gap-1.5 bg-orange-500 rounded-full px-3 py-1 shrink-0">
                          <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><polyline points="20 6 9 17 4 12" /></svg>
                          <span className="text-white text-xs font-medium">{fmtTime(selectedSlot.slot_time || selectedSlot.start_time)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white px-5 pt-5 pb-4 space-y-5">
                    {slotType !== 'manual' ? (
                      <>
                        {/* Date chips */}
                        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                          {getNextDates(7).map(date => {
                            const dateStr = date.toISOString().slice(0, 10);
                            const isActive = pickedDate === dateStr;
                            const isToday = dateStr === new Date().toISOString().slice(0, 10);
                            return (
                              <button
                                key={dateStr}
                                onClick={() => { setPickedDate(dateStr); loadSlots(orderId, dateStr, showNotification); }}
                                disabled={loadingSlots}
                                className={`flex flex-col items-center gap-0.5 rounded-2xl shrink-0 transition-all disabled:opacity-40 px-5 py-3.5 min-w-[64px] border mt-3 ${isActive
                                  ? 'bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-200 scale-105'
                                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-orange-50 hover:border-orange-200'
                                  }`}
                              >
                                <span className={`text-[11px] font-semibold uppercase tracking-wide ${isActive ? 'text-orange-100' : 'text-gray-400'}`}>
                                  {isToday ? 'Today' : date.toLocaleDateString('en-IN', { weekday: 'short' })}
                                </span>
                                <span className="text-xl font-bold leading-none">{date.getDate()}</span>
                                <span className={`text-[11px] font-medium ${isActive ? 'text-orange-200' : 'text-gray-400'}`}>{date.toLocaleDateString('en-IN', { month: 'short' })}</span>
                              </button>
                            );
                          })}
                        </div>

                        {/* Time slots */}
                        {loadingSlots ? (
                          <div className="flex items-center justify-center py-12">
                            <div className={`animate-spin rounded-full h-7 w-7 border-2 border-t-transparent ${T.spinner}`} />
                          </div>
                        ) : !pickedDate ? (
                          <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
                            <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center">
                              <svg className="w-5 h-5 text-orange-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                            </div>
                            <p className="text-sm text-gray-400">Select a date above to see available slots</p>
                          </div>
                        ) : slots.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
                            <p className="text-sm font-medium text-gray-500">No slots available</p>
                            <p className="text-xs text-gray-400">Try a different date</p>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Available Times</p>
                              <span className="text-xs font-medium text-orange-500 bg-orange-50 border border-orange-100 px-2 py-0.5 rounded-full">{slots.length} slots</span>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              {slots.map(slot => {
                                const slotKey = String(slot.id || slot.slot_id || slot.slot || '');
                                const savedId = savedSlotIdRef.current || String(selectedSlot?.slot_id || selectedSlot?.id || '');
                                const savedTime = selectedSlot?.slot_time || selectedSlot?.start_time || '';
                                const isActive = (!!savedId && slotKey === savedId) ||
                                  (!!savedTime && !!slot.start_time && slot.start_time === savedTime);
                                return (
                                  <button
                                    key={slotKey}
                                    onClick={() => { savedSlotIdRef.current = slotKey; setSelectedSlot({ ...slot, slot_id: slotKey, slot_time: slot.start_time || '' }); }}
                                    className={`relative py-3.5 px-4 rounded-xl text-center transition-all active:scale-[0.97] border ${isActive
                                      ? 'bg-orange-500 border-orange-500 shadow-md shadow-orange-200 scale-[1.04]'
                                      : 'bg-white border-gray-200 hover:border-orange-300 hover:bg-orange-50/60'
                                      }`}
                                  >
                                    {isActive && (
                                      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-white opacity-80" />
                                    )}
                                    <p className={`text-sm font-bold ${isActive ? 'text-white' : 'text-gray-800'} leading-tight`}>
                                      {fmtTime(slot.start_time)}{slot.end_time ? <span className={`font-normal ${isActive ? 'text-orange-100' : 'text-gray-400'}`}> – {fmtTime(slot.end_time)}</span> : ''}
                                    </p>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Collection Date *</label>
                          <input type="date" className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all" value={manualDate} onChange={e => setManualDate(e.target.value)} />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Collection Time *</label>
                          <input type="time" className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all" value={manualTime} onChange={e => setManualTime(e.target.value)} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-3.5 bg-white border-t border-gray-100">
                    <NavRow
                      onBack={() => goToStep(3)}
                      onNext={handleSaveSlot}
                      nextLabel="Save Slot & Continue"
                      nextDisabled={slotType === 'manual' ? (!manualDate || !manualTime) : !selectedSlot}
                      nextLoading={savingSlot}
                    />
                  </div>
                </div>
              );
            })()}

            {/* ── Step 5: Review & Push ── */}
            {step === 5 && (
              <div className="rounded-2xl overflow-hidden shadow-sm border border-orange-100">

                {/* Header */}
                <div className="bg-orange-50 px-5 py-4 border-b border-orange-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-gray-800 font-semibold text-base leading-tight">Review &amp; Push</h3>
                      <p className="text-gray-400 text-xs mt-0.5">Confirm all details before placing the order</p>
                    </div>
                    {cart && (
                      <div className="flex items-center gap-1.5 bg-orange-500 rounded-full px-3 py-1 shrink-0">
                        <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                        <span className="text-white text-xs font-medium">{(cart.members || []).reduce((n, m) => n + (m.products || []).length, 0)} tests</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white p-4">
                  {!cart ? (
                    <div className="flex justify-center py-20">
                      <div className={`animate-spin rounded-full h-10 w-10 border-b-2 ${T.spinner}`} />
                    </div>
                  ) : (
                    <div className="grid grid-cols-5 gap-4 items-start">

                      {/* LEFT: Tests by patient */}
                      <div className="col-span-3 bg-white rounded-2xl border border-orange-100 shadow-sm overflow-hidden">
                        <div className="px-5 py-3.5 border-b border-orange-50 flex items-center justify-between">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Tests Ordered</p>
                          <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2.5 py-0.5 rounded-full">
                            {(cart.members || []).reduce((n, m) => n + (m.products || []).length, 0)} tests
                          </span>
                        </div>
                        <div className="divide-y divide-gray-50 max-h-[420px] overflow-y-auto orange-scroll">
                          {(cart.members || []).map(member => (
                            <div key={member.id} className="px-5 py-4">
                              {/* Member header */}
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-xs font-bold shrink-0">
                                  {member.name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-800">{member.name}</p>
                                  {member.relation && <p className="text-xs text-gray-400 capitalize mt-0.5">{member.relation}</p>}
                                </div>
                                <span className="text-xs font-medium text-orange-500 bg-orange-50 border border-orange-100 px-2 py-0.5 rounded-md shrink-0">
                                  {(member.products || []).length} test{(member.products || []).length !== 1 ? 's' : ''}
                                </span>
                              </div>
                              {/* Test list */}
                              {(member.products || []).length === 0 ? (
                                <p className="text-xs text-gray-300 italic ml-11">No tests added</p>
                              ) : (
                                <div className="ml-11 mt-1">
                                  {(member.products || []).map((p, i, arr) => (
                                    <div key={i} className={`flex items-center justify-between py-2 ${i < arr.length - 1 ? 'border-b border-orange-100' : ''}`}>
                                      <span className="text-sm text-gray-600 truncate pr-3">{p.name}</span>
                                      <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0">{fmt(p.mrp)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* RIGHT: Details + Bill + CTA */}
                      <div className="col-span-2 space-y-3">

                        {/* Slot */}
                        {cart.slot_date && (
                          <div className="bg-white rounded-2xl border border-orange-100 shadow-sm px-4 py-3.5">
                            <div className="flex items-center gap-2 mb-1.5">
                              <svg className="w-3.5 h-3.5 text-orange-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Collection Slot</p>
                            </div>
                            <p className="text-sm font-semibold text-gray-800">{cart.slot_date}</p>
                            {cart.slot_time && <p className="text-xs text-orange-500 mt-0.5 font-medium">{fmtTime(cart.slot_time)}</p>}
                          </div>
                        )}

                        {/* Address */}
                        {cart.address?.address_line_1 && (
                          <div className="bg-white rounded-2xl border border-orange-100 shadow-sm px-4 py-3.5">
                            <div className="flex items-center gap-2 mb-1.5">
                              <svg className="w-3.5 h-3.5 text-orange-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" /><circle cx="12" cy="9" r="2.5" /></svg>
                              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Address</p>
                            </div>
                            <p className="text-sm font-semibold text-gray-800 capitalize leading-snug">{cart.address.address_line_1}</p>
                            {(cart.address.sublocality || cart.address.landmark) && (
                              <p className="text-xs text-gray-500 mt-0.5 capitalize">
                                {[cart.address.landmark && `Near ${cart.address.landmark}`, cart.address.sublocality].filter(Boolean).join(', ')}
                              </p>
                            )}
                            {(cart.address.city || cart.address.state || cart.address.pincode) && (
                              <p className="text-xs text-gray-400 mt-0.5 capitalize">
                                {[cart.address.city, cart.address.state].filter(Boolean).join(', ')}
                                {cart.address.pincode && <span className="ml-1.5 font-medium text-gray-500">{cart.address.pincode}</span>}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Collection Type */}
                        {(cart.home_collection || cart.walkin) && (
                          <div className="bg-white rounded-2xl border border-orange-100 shadow-sm px-4 py-3.5">
                            <div className="flex items-center gap-2 mb-1.5">
                              <svg className="w-3.5 h-3.5 text-orange-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Collection Type</p>
                            </div>
                            <p className="text-sm font-semibold text-gray-800">
                              {cart.home_collection ? 'Home Collection' : 'Walk-in at Centre'}
                            </p>
                          </div>
                        )}

                        {/* Bill */}
                        <div className="bg-white rounded-2xl border border-orange-100 shadow-sm px-4 py-4">
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Bill Summary</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between text-gray-500">
                              <span>Item Total</span>
                              <span className="tabular-nums">{fmt(cart.subtotal)}</span>
                            </div>
                            <div className="flex justify-between text-gray-500">
                              <span>{chargeLine?.label || 'Delivery'}</span>
                              {chargeLine?.isAyushpayLab && chargeLine?.amount === 0
                                ? <span className="text-emerald-600 font-semibold">Free</span>
                                : <span className="tabular-nums">{fmt(chargeLine?.amount ?? cart.delivery_charges)}</span>
                              }
                            </div>
                            {cart.handling_packaging_charge > 0 && (
                              <div className="flex justify-between text-gray-500">
                                <span>Handling</span>
                                <span className="tabular-nums">{fmt(cart.handling_packaging_charge)}</span>
                              </div>
                            )}
                            {cart.platform_charge > 0 && (
                              <div className="flex justify-between text-gray-500">
                                <span>Platform</span>
                                <span className="tabular-nums">{fmt(cart.platform_charge)}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex justify-between items-center mt-3 pt-3 border-t border-orange-100">
                            <span className="text-sm font-semibold text-gray-500">Bill Amount</span>
                            <span className="text-2xl font-extrabold text-orange-600 tabular-nums">{fmt(cart.final_payable_amount)}</span>
                          </div>
                          {cart.eligible_cashback > 0 && (
                            <div className="flex items-center justify-between mt-3 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-green-500 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                                <span className="text-sm font-semibold text-green-700">Instant Cashback</span>
                              </div>
                              <span className="text-base font-bold text-green-600 tabular-nums">{fmt(cart.eligible_cashback)}</span>
                            </div>
                          )}
                        </div>

                        {/* CTA */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => goToStep(4)}
                            disabled={submitting}
                            className="px-3 py-2.5 rounded-xl text-sm font-medium border border-orange-200 bg-white text-gray-600 hover:bg-orange-50 transition-colors shadow-sm shrink-0"
                          >
                            ← Back
                          </button>
                          <button
                            onClick={handleGeneratePaymentLink}
                            disabled={paymentLinkDisabled}
                            title={isOrderPaid ? 'Payment already received for this order' : 'Sends a Razorpay link for the amount after cashback'}
                            className={`px-3 py-2.5 rounded-xl text-sm font-bold border transition-colors shrink-0 ${paymentLinkDisabled
                              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                              : 'border-orange-500 text-orange-600 bg-white hover:bg-orange-50 shadow-sm'
                              }`}
                          >
                            {generatingLink
                              ? <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /> Generating…</span>
                              : 'Generate Payment Link'
                            }
                          </button>
                          <button
                            onClick={handleSubmit}
                            disabled={submitting || generatingLink || !cart.final_payable_amount}
                            className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-[0.98] ${submitting || generatingLink || !cart.final_payable_amount
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-200'
                              }`}
                          >
                            {submitting
                              ? <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Pushing…</span>
                              : <span>Push Order · {fmt(cart.final_payable_amount)}</span>
                            }
                          </button>
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Step 6: Success ── */}
            {step === 6 && (
              <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-white">

                {/* Top stripe */}
                <div className="h-1 bg-orange-500" />

                <div className="px-10 py-10">

                  {/* Status row */}
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 rounded-2xl bg-orange-500 flex items-center justify-center shadow-md shadow-orange-200 shrink-0">
                      <FaCheck className="text-white" size={22} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-orange-500 uppercase tracking-widest mb-0.5">Confirmed</p>
                      <h2 className="text-2xl font-extrabold text-gray-900 leading-tight">Order Pushed Successfully</h2>
                    </div>
                  </div>

                  {/* Order ID + divider */}
                  {orderNumber && (
                    <div className="flex items-center gap-3 mb-8">
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Order ID</span>
                      <span className="h-px flex-1 bg-gray-100" />
                      <span className="text-sm font-bold text-gray-800 font-mono">#{orderNumber}</span>
                    </div>
                  )}

                  {paymentLink && !isOrderPaid && (
                    <div className="mb-8 -mt-4">
                      <PaymentLinkCard
                        link={paymentLink}
                        onResend={handleResendPaymentLink}
                        resending={generatingLink}
                      />
                    </div>
                  )}

                  {/* Pushed without a link: the order is already pushed, so this
                      only asks the backend for a link — it re-sends an unexpired one for the same
                      amount, else creates one — and the card above then takes over. */}
                  {!paymentLink && !isOrderPaid && (
                    <button
                      onClick={handleResendPaymentLink}
                      disabled={generatingLink || !orderId}
                      title="Sends a Razorpay link for the amount after cashback"
                      className={`w-full mb-8 -mt-4 py-2.5 rounded-xl text-sm font-bold border transition-colors ${generatingLink || !orderId
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'border-orange-500 text-orange-600 bg-white hover:bg-orange-50 shadow-sm'
                        }`}
                    >
                      {generatingLink
                        ? <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /> Sending…</span>
                        : 'Send Payment Link'
                      }
                    </button>
                  )}

                  {/* Timeline */}
                  <div className="space-y-0 mb-8">
                    {[
                      { n: 1, title: 'Tests Confirmed', desc: 'All selected tests are booked with the partner lab.', done: true },
                      { n: 2, title: 'Sample Collection', desc: 'A phlebotomist will visit the address on the scheduled slot.', done: false },
                      { n: 3, title: 'Reports Delivered', desc: 'Digital reports ready within 24 – 48 hours of collection.', done: false },
                    ].map(({ n, title, desc, done }, i, arr) => (
                      <div key={n} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${done ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                            {done ? <FaCheck size={10} /> : n}
                          </div>
                          {i < arr.length - 1 && <div className="w-px flex-1 bg-gray-100 my-1" />}
                        </div>
                        <div className={`pb-6 ${i === arr.length - 1 ? 'pb-0' : ''}`}>
                          <p className={`text-sm font-semibold leading-tight ${done ? 'text-gray-900' : 'text-gray-400'}`}>{title}</p>
                          <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => navigate(`/subscriber-wallet/subscriber/${id}`)}
                    className="w-full py-3 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 active:scale-[0.99] transition-all shadow-sm shadow-orange-200"
                  >
                    Back to Subscriber
                  </button>
                </div>
              </div>
            )}

          </div>{/* end step slide wrapper */}

          {/* ── Notification toast ── */}
          {notification && (
            <div className="fixed top-4 right-4 z-50 max-w-md">
              <div className={`flex items-start gap-2 px-4 py-3 rounded-md shadow-lg ${notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800'
                : notification.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800'
                  : 'bg-orange-50 border border-orange-200 text-orange-800'
                }`}>
                {notification.type === 'success'
                  ? <FaCheckCircle className="mt-0.5 shrink-0" />
                  : <FaExclamationCircle className="mt-0.5 shrink-0" />
                }
                <div className="text-sm">{notification.message}</div>
                <button onClick={() => setNotification(null)} className="ml-2 opacity-60 hover:opacity-100"><FaTimes size={12} /></button>
              </div>
            </div>
          )}
        </>
      </div>
    </div>
  );
};

export default DiagnosticTransaction;
